import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useArchiveBinderEntry, useBinderEntry } from "../properties/binder-api.js";
import { BinderMediaThumb } from "../properties/BinderMediaThumb.js";
import { deriveAge, dateKindLabel, entryTypeLabels, formatApproxDate } from "../properties/binder-templates.js";

export function BinderEntryDetail() {
  const { id, entryId } = useParams<{ id: string; entryId: string }>();
  const navigate = useNavigate();
  const { data: entry, isLoading, error } = useBinderEntry(id, entryId);
  const archiveMutation = useArchiveBinderEntry(id ?? "");
  const [confirming, setConfirming] = useState(false);

  if (!id || !entryId) return <Navigate to="/app/properties" replace />;
  if (isLoading) return <p className="p-4 text-sm text-slate-500">Loading…</p>;
  if (error || !entry) {
    return (
      <div className="p-4">
        <p className="rounded bg-rose-50 p-3 text-sm text-rose-900">Entry not found.</p>
        <Link to={`/app/properties/${id}`} className="mt-3 inline-block text-sm text-teal-700">
          Back to property
        </Link>
      </div>
    );
  }

  const facts = Object.entries(entry.facts ?? {});
  const dates = Object.entries(entry.dates ?? {});

  return (
    <div>
      <header className="border-b border-slate-200 bg-white px-4 py-3">
        <Link to={`/app/properties/${id}`} className="text-sm text-teal-700">
          ‹ Property
        </Link>
        <h1 className="mt-1 text-xl font-semibold">{entry.title}</h1>
        <div className="mt-1.5 flex flex-wrap gap-2">
          <Chip>{entryTypeLabels[entry.entryType]}</Chip>
          {entry.locationLabel && <Chip>{entry.locationLabel}</Chip>}
        </div>
      </header>

      <div className="space-y-4 p-4">
        {entry.media && entry.media.length > 0 && (
          <ul className="grid grid-cols-3 gap-2">
            {entry.media.map((m) => (
              <li key={m.id}>
                <BinderMediaThumb propertyId={id} entryId={entry.id} mediaId={m.id} />
              </li>
            ))}
          </ul>
        )}

        {facts.length > 0 && (
          <dl className="overflow-hidden rounded-md border border-slate-200">
            {facts.map(([key, f], i) => (
              <div
                key={key}
                className={`flex items-center justify-between px-3 py-2.5 ${
                  i > 0 ? "border-t border-slate-200" : ""
                }`}
              >
                <dt className="text-sm text-slate-500">{f.label}</dt>
                <dd className="text-sm font-medium text-slate-900">
                  {typeof f.value === "boolean" ? (f.value ? "Yes" : "No") : String(f.value)}
                  {f.unit ? ` ${f.unit}` : ""}
                </dd>
              </div>
            ))}
          </dl>
        )}

        {dates.length > 0 && (
          <dl className="space-y-1.5">
            {dates.map(([key, d]) => {
              const age = deriveAge(d);
              return (
                <div key={key} className="flex items-baseline justify-between">
                  <dt className="text-sm text-slate-500">{dateKindLabel(key)}</dt>
                  <dd className="text-sm text-slate-900">
                    {formatApproxDate(d)}
                    {age ? <span className="text-slate-500"> · {age}</span> : null}
                  </dd>
                </div>
              );
            })}
          </dl>
        )}

        {entry.notes && (
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Notes</p>
            <p className="mt-0.5 whitespace-pre-wrap text-sm text-slate-900">{entry.notes}</p>
          </div>
        )}

        {entry.tags && entry.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {entry.tags.map((t) => (
              <Chip key={t}>{t}</Chip>
            ))}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <Link
            to={`/app/properties/${id}/binder/${entry.id}/edit`}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-md border border-slate-300 bg-white text-sm font-medium text-slate-700"
          >
            Edit
          </Link>
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="min-h-[44px] flex-1 rounded-md border border-rose-200 bg-white text-sm font-medium text-rose-700"
          >
            Archive
          </button>
        </div>
      </div>

      {confirming && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-20 flex items-end bg-slate-900/40 px-4 pb-[max(env(safe-area-inset-bottom),1rem)]"
        >
          <div className="w-full rounded-lg bg-white p-4 shadow-xl">
            <h2 className="text-base font-semibold">Archive this entry?</h2>
            <p className="mt-1 text-sm text-slate-600">
              It will be hidden from your property file. You can ask us to restore it later.
            </p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="min-h-[44px] flex-1 rounded-md border border-slate-300 bg-white text-sm font-medium text-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={archiveMutation.isPending}
                onClick={async () => {
                  await archiveMutation.mutateAsync(entry.id);
                  navigate(`/app/properties/${id}`, { replace: true });
                }}
                className="min-h-[44px] flex-1 rounded-md bg-rose-600 text-sm font-medium text-white disabled:opacity-60"
              >
                {archiveMutation.isPending ? "Archiving…" : "Archive"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
      {children}
    </span>
  );
}
